import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoChatbubbleOutline } from 'react-icons/io5';
import { conversationPath } from '../../../../app/routes/paths';
import { useRolePath } from '../../../../app/routes/useRolePath';
import { showErrorToast } from '../../../../app/components/SnackBar/SnackBar';
import { useGetMentorByIdQuery } from '../../../mentor/infrastructure/api/mentor.api';
import { useGetStudentByIdQuery } from '../../../student/infrastructure/api/student-profile.api';
import { useNotificationsCount } from '../../../notification/ui/hooks/useNotificationsCount';
import { useUserProfile } from '../../../user/ui/hooks/useUserProfile';
import {
  useGetConversationQuery,
  useListConversationMessagesQuery,
  useListConversationsQuery,
  useMarkConversationReadMutation,
  useSendConversationMessageMutation,
} from '../../infrastructure/api/conversation.api';
import type { Conversation, ConversationPeer } from '../../application/types';
import './ConversationPage.scss';

type LocationState = {
  peerName?: string;
  peerAvatarUrl?: string | null;
  mentorSlug?: string;
};

function previewOf(body: string | null | undefined): string {
  const text = body?.trim() ?? '';
  if (!text) return '';
  return text;
}

function peerPersonName(
  row: Pick<Conversation, 'peerFirstName' | 'peerLastName'> | undefined,
): string {
  if (!row) return '';
  return [row.peerFirstName, row.peerLastName].filter(Boolean).join(' ').trim();
}

export default function ConversationPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const to = useRolePath();
  const location = useLocation();
  const peerState = (location.state ?? {}) as LocationState;
  const { profile } = useUserProfile();
  const selectedId = Number(id);
  const hasSelected = Number.isInteger(selectedId) && selectedId > 0;
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError } = useListConversationsQuery({});
  const { conversations: unreadConversations } = useNotificationsCount();
  const unreadByConversation = useMemo(() => {
    const counts = new Map<number, number>();
    for (const row of unreadConversations) counts.set(row.id, row.count);
    return counts;
  }, [unreadConversations]);
  const conversations = data?.list ?? [];
  const visibleConversations = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) return conversations;
    return conversations.filter((row) =>
      peerPersonName(row).toLocaleLowerCase().includes(needle),
    );
  }, [conversations, search]);
  const { data: selectedConversation } = useGetConversationQuery(selectedId, {
    skip: !hasSelected,
  });
  const viewingAsStudent = Boolean(profile?.student);
  const { data: peerMentor } = useGetMentorByIdQuery(
    selectedConversation?.mentorId ?? 0,
    { skip: !hasSelected || !viewingAsStudent || !selectedConversation?.mentorId },
  );
  const { data: peerStudent } = useGetStudentByIdQuery(
    selectedConversation?.studentId ?? 0,
    { skip: !hasSelected || viewingAsStudent || !selectedConversation?.studentId },
  );
  const {
    data: messagesPage,
    isLoading: messagesLoading,
  } = useListConversationMessagesQuery(
    { conversationId: selectedId },
    { skip: !hasSelected },
  );
  const [sendMessage, { isLoading: sending }] =
    useSendConversationMessageMutation();
  const [markRead] = useMarkConversationReadMutation();

  const peer: ConversationPeer = useMemo(() => {
    const listedName = peerPersonName(
      conversations.find((row) => row.id === selectedId),
    );
    if (listedName) {
      const listedAvatar = viewingAsStudent
        ? peerMentor?.profile?.avatarUrl ?? null
        : peerStudent?.profile?.avatarUrl ?? null;
      return { name: listedName, avatarUrl: listedAvatar };
    }
    const mentorName = peerMentor?.profile?.displayName;
    const studentName = peerStudent?.profile?.displayName;
    const loadedName = viewingAsStudent ? mentorName : studentName;
    const loadedAvatar = viewingAsStudent
      ? peerMentor?.profile?.avatarUrl ?? null
      : peerStudent?.profile?.avatarUrl ?? null;
    if (loadedName) return { name: loadedName, avatarUrl: loadedAvatar };
    if (peerState.peerName) {
      return {
        name: peerState.peerName,
        avatarUrl: peerState.peerAvatarUrl ?? null,
      };
    }
    return { name: t('conversation.peer'), avatarUrl: null };
  }, [
    peerMentor?.profile?.avatarUrl,
    peerMentor?.profile?.displayName,
    peerState.peerAvatarUrl,
    peerState.peerName,
    peerStudent?.profile?.avatarUrl,
    peerStudent?.profile?.displayName,
    conversations,
    selectedId,
    t,
    viewingAsStudent,
  ]);

  const lastMessageId = messagesPage?.items[messagesPage.items.length - 1]?.id;

  useEffect(() => {
    if (!hasSelected || lastMessageId == null) return;
    markRead({ conversationId: selectedId, messageId: lastMessageId });
  }, [hasSelected, lastMessageId, markRead, selectedId]);

  function openConversation(conversationId: number) {
    navigate(to(conversationPath(conversationId)));
  }

  function onSend() {
    const body = draft.trim();
    if (!body || !hasSelected) return;
    sendMessage({ conversationId: selectedId, body })
      .unwrap()
      .then(() => {
        setDraft('');
      })
      .catch(() => {
        showErrorToast(t('conversation.sendError'));
      });
  }

  return (
    <div className="conversation-page">
      <aside className="conversation-page__list">
        <h1 className="conversation-page__list-title">{t('conversation.title')}</h1>
        <div className="conversation-page__search">
          <input
            type="search"
            value={search}
            placeholder={t('conversation.search')}
            aria-label={t('conversation.search')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {isLoading ? (
          <p className="conversation-page__list-status">{t('conversation.loading')}</p>
        ) : null}
        {isError ? (
          <p className="conversation-page__list-status">{t('conversation.error')}</p>
        ) : null}
        {!isLoading && !isError && conversations.length === 0 ? (
          <p className="conversation-page__list-status">{t('conversation.emptyList')}</p>
        ) : null}
        {!isLoading && !isError && conversations.length > 0 && visibleConversations.length === 0 ? (
          <p className="conversation-page__list-status">{t('conversation.searchEmpty')}</p>
        ) : null}
        <ul className="conversation-page__items">
          {visibleConversations.map((row) => {
            const active = row.id === selectedId;
            const title = peerPersonName(row) || t('conversation.peer');
            const badgeCount = unreadByConversation.get(row.id) ?? 0;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  className={
                    active
                      ? 'conversation-page__item conversation-page__item--active'
                      : 'conversation-page__item'
                  }
                  onClick={() => openConversation(row.id)}
                >
                  <span className="conversation-page__item-body">
                    <span className="conversation-page__item-name">{title}</span>
                    <span className="conversation-page__item-preview">
                      {previewOf(row.lastMessage?.body) || t('conversation.noMessages')}
                    </span>
                  </span>
                  {badgeCount > 0 ? (
                    <span className="conversation-page__badge">{badgeCount}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <section className="conversation-page__thread">
        {!hasSelected ? (
          <div className="conversation-page__thread-empty" role="status">
            <IoChatbubbleOutline
              className="conversation-page__thread-empty-icon"
              aria-hidden
            />
            <p className="conversation-page__thread-empty-title">
              {t('conversation.emptyThreadTitle')}
            </p>
            <p className="conversation-page__thread-empty-hint">
              {t('conversation.emptyThreadHint')}
            </p>
          </div>
        ) : (
          <>
            <header className="conversation-page__thread-head">{peer.name}</header>
            {messagesLoading && !messagesPage ? (
              <p className="conversation-page__thread-status">
                {t('conversation.loading')}
              </p>
            ) : (
              <ul className="conversation-page__messages">
                {(messagesPage?.items ?? []).length === 0 ? (
                  <li className="conversation-page__thread-status">
                    {t('conversation.noMessages')}
                  </li>
                ) : (
                  (messagesPage?.items ?? []).map((message) => {
                    const mine = message.senderId === profile?.id;
                    return (
                      <li
                        key={message.id}
                        className={
                          mine
                            ? 'conversation-page__bubble conversation-page__bubble--mine'
                            : 'conversation-page__bubble'
                        }
                      >
                        <p>{message.body}</p>
                        <time>
                          {message.createdAt
                            ? new Date(message.createdAt).toLocaleTimeString(
                                i18n.language.toLowerCase().startsWith('ua')
                                  ? 'uk-UA'
                                  : i18n.language,
                                { hour: '2-digit', minute: '2-digit' },
                              )
                            : ''}
                        </time>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
            <form
              className="conversation-page__composer"
              onSubmit={(event) => {
                event.preventDefault();
                onSend();
              }}
            >
              <textarea
                className="conversation-page__input"
                rows={1}
                value={draft}
                placeholder={t('conversation.placeholder')}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    onSend();
                  }
                }}
              />
              <button
                type="submit"
                className="conversation-page__send"
                disabled={sending || !draft.trim()}
              >
                {t('conversation.send')}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
